/**
 * API payload types shared by several API modules.
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165).
 */
import type { OverlayRole } from '@brainfocus/web/domain/neuroimage/overlay_roles';
import { NormType } from '@brainfocus/web/analysis/models';
import { ChannelHeader, ChannelKind } from '@brainfocus/web/utils/channel_kind';
import { Montage } from '@brainfocus/web/domain/montage/montage';

export interface AnalysisDetail {
    id: number;
    label: string;
    uploader: string;
    montage: Montage;
    patientId: number;
    patientName: string;
    status: AnalysisStatus;
    createdOn: Date;
    edfId: number;
}

export type AnalysisStatus = 'RecordingStatus.PENDING_ANALYSIS' | 'RecordingStatus.PROCESSING' | 'RecordingStatus.DONE' | 'RecordingStatus.FAILED' | 'RecordingStatus.ABORTED';

/**
 * Which SOZ-detection methodology produced (or will produce) an analysis's
 * results (#922). See {@link Analysis#detectionMethod}.
 */
export type DetectionMethod = 'moving_zscore' | 'cumulative_percentile';

export interface SeizureGroup {
    id: string;
    name: string;
}

/**
 * Per-electrode colormap range override, percentiles in [0, 100].
 */
export interface ElectrodeColormapRange {
    min: number;
    max: number;
}

/** Per-electrode colormap range overrides, keyed by electrode label. */
export type ElectrodeColormapRanges = Record<string, ElectrodeColormapRange>;

/**
 * Analysis (recording) representation returned by the API.
 */
export interface Analysis {
    id: number;
    edfId: number;
    label: string;
    /**
     * User-selected analysis onset time
     */
    onset: number;
    /**
     * User-selected analysis duration
     */
    duration: number;
    /**
     * Seizure onset time selected on upload. `null` when the analysis was
     * launched without selecting a seizure onset (#656).
     */
    seizureOnset: number | null;
    /**
     * Seizure onset duration selected on upload. `null` under the same
     * condition as {@link seizureOnset}.
     */
    seizureDuration: number | null;
    /**
     * Seizure onset the analysis was created with (#721). MODIFY moves
     * {@link seizureOnset}, so this is what "Reset" goes back to. `null` or absent
     * for analyses created before it was stored.
     */
    originalOnset?: number | null;
    /**
     * Analysis window duration the analysis was created with (#721). Companion of
     * {@link originalOnset}.
     */
    originalDuration?: number | null;
    status: AnalysisStatus;
    createdOn: string;
    badChannels: number[];
    cleanChannels: number[];
    candidateChannels: number[];
    /**
     * User-assigned early-propagation channels (issue #359). Optional because
     * older fixtures and demo data may pre-date the field.
     */
    earlyPropagationChannels?: number[];
    /**
     * User-assigned late-propagation channels (issue #359). Optional for the
     * same reason as `earlyPropagationChannels`.
     */
    latePropagationChannels?: number[];
    diverged: boolean;
    /**
     * Reason the analysis diverged, when known (#733). `'norm_type'` means the
     * SAM normalization mode was changed and the automatic-detection result is
     * stale. `null` for the generic (input-change) divergence or none.
     */
    divergedReason?: string | null;
    /**
     * SAM / automatic-detection normalization mode stored per-recording (#733).
     * `null` for legacy analyses predating the field — the resolver treats that
     * as {@link DEFAULT_NORM_TYPE}.
     */
    normType?: NormType | null;
    montage: Montage | null;
    /**
     * User-facing cause of the last failed run, persisted by the worker (#748).
     * `null` for healthy analyses and for rows that failed before the field
     * existed. Optional because older fixtures pre-date it.
     */
    failureReason?: string | null;
    /**
     * Channel labels the analysis was originally run over (#722/#748), or
     * `null` when the selection was not persisted. Drives the editable retry.
     */
    analysisChannels?: string[] | null;
    demo: boolean;
    thresholdGa: number;
    thresholdAe: number;
    /**
     * Which SOZ-detection methodology produced (or will produce) this
     * analysis's results (#922). `"moving_zscore"` = fixed-window/z-score/
     * focal-checkbox methodology. `null`/`"cumulative_percentile"` = the
     * legacy cumulative-window/percentile/spread-slider methodology.
     */
    detectionMethod?: DetectionMethod | null;
    /**
     * GA z-score threshold for the moving-window methodology (#922). Only
     * meaningful when {@link detectionMethod} is `"moving_zscore"`.
     */
    zscoreThreshold?: number | null;
    /**
     * Channel-activation scope for the moving-window methodology (#922):
     * `true` = "Narrow channel activation", `false` = "Broad channel
     * activation" (field name `isFocal`). Only meaningful when
     * {@link detectionMethod} is `"moving_zscore"`.
     */
    isFocal?: boolean | null;
    /**
     * Minimum flagged-run duration gate for the moving-window methodology
     * (#943): `true`/`null`/undefined keeps the original behavior (a
     * channel's continuous flagged run must span at least 1s to be
     * reported); `false` reports every majority-voted run regardless of
     * duration. Only meaningful when {@link detectionMethod} is
     * `"moving_zscore"`.
     */
    minDurationEnforced?: boolean | null;
    /**
     * Whether automatic detection is extended backward to cover the
     * pre-onset period already cropped/stored for baseline (#943). Unlike
     * the other moving-methodology controls, changing this requires a
     * recompute (the stored data simply does not cover that range otherwise)
     * -- saving it diverges the analysis via the normal mechanism instead of
     * re-scoring live. Only meaningful when {@link detectionMethod} is
     * `"moving_zscore"`.
     */
    preOnsetDetection?: boolean | null;
    /**
     * Display-only z-score equivalent of a legacy analysis's stored GA
     * percentile (#922) -- computed server-side on every read, never
     * persisted. `null` for a moving-window-methodology analysis.
     */
    legacyZscoreEquivalent?: number | null;
    /**
     * Per-electrode colormap range overrides for the METRICS/Explore TC plot
     * (#922 follow-up), keyed by electrode label (e.g. "LA"). An electrode
     * absent from this map uses the panel's own global colormap range.
     * `null`/undefined means no electrode has been customized.
     */
    electrodeColormapRanges?: ElectrodeColormapRanges | null;
    uploaderName?: string;
    patientName?: string;
    institutionName?: string;
    institutionId?: number;
    readonly: boolean;
    /**
     * User-written notes for the analysis. The notes may include \n characters
     * which should be translated correctly in HTML.
     */
    notes: string;
    group: SeizureGroup | null;
    /**
     * Whether to show algorithm output (highlights, markers, aSOZ tags) in the analysis view
     */
    showAlgorithmOutput: boolean;
    /**
     * Whether to show EDF annotations (cSOZ tags, yellow lines) in the analysis view
     */
    showEdfAnnotations: boolean;
    /** Metrics EEG overlay switch (#1109). Absent in analyses cached before it existed: read as ON. */
    metricsEegOverlay?: boolean;
    /** Metrics overlay gain; `null` means "start at the EEG gain" (#1109). */
    metricsEegOverlayGain?: number | null;
    /** Metrics above-threshold shading switch (#1109). Absent reads as OFF. */
    metricsShading?: boolean;
    /** Metrics shading threshold, in standard deviations from baseline; `null` means the default (#1109). */
    metricsShadingThreshold?: number | null;
    mriRecord: number | null;
    patientId: number;
    /**
     * Whether this analysis is ephemeral (trial run that is deleted when the
     * session ends). Optional for backwards compatibility with legacy payloads.
     */
    ephemeral?: boolean;
    /**
     * Whether the current user may launch an ephemeral run from this (readonly
     * demo) analysis. Optional for backwards compatibility.
     */
    canRunEphemeral?: boolean;
    /**
     * Whether annotations on this analysis's recording are read-only for the
     * current user (#900).
     *
     * Distinct from {@link readonly}, which is the *analysis*-level access level.
     * Annotations hang off the recording (`UploadedEdf`), so an ephemeral analysis
     * a trial user created over demo material is writable as an analysis while its
     * annotations are not. Optional for backwards compatibility with legacy
     * payloads; absent reads as writable, matching the previous behaviour.
     */
    annotationsReadonly?: boolean;
    /**
     * i718: true when this DONE analysis predates the tiered baseline fallback
     * and was blocked purely by a too-short baseline (calc_meta has
     * `baseline_reason: 'recording_too_short'` and no `baseline_tier` yet) —
     * it can now be retried to pick up a reduced/minimal baseline instead of
     * none. False once recomputed (gains a `baseline_tier`) or for any other
     * no-baseline reason. Optional for backwards compatibility.
     */
    eligibleForBaselineRecompute?: boolean;
    /**
     * Whether the analysis can be fully re-run (it stores its channel
     * selection, #722). False for analyses computed on v0.13.x, which retry()
     * refuses — no Recompute may be offered for them (#960 V101). Optional
     * for backwards compatibility.
     */
    canFullyReanalyze?: boolean;
}

/**
 * Recording (uploaded EDF) representation returned by the API.
 */
export interface Recording {
    id: number;
    filename: string;
    title: string;
    acquisitionDate: string;
    duration: number;
    patientId: number;
    analysisCount: number;
    hasUnfinishedAnalysis: boolean;
    /** Companion videos attached to this recording (empty when none) (#743). */
    videos: TrcVideoRecord[];
    /** When true, the recording is demo/read-only and cannot be deleted or renamed. */
    readonly?: boolean;
}

/** Persisted video record returned by the videos endpoint (no storage ref). */
export interface TrcVideoRecord {
    id: number;
    uploadedEdfId: number;
    originalFilename: string;
    filenameNum: number | null;
    offsetSec: number;
    durationSec: number;
}

export interface GetEegPayload {
    recordingId: number;
    channels: number[];
}

export interface ListManagedPayload {
    offset: number;
    limit: number;
}

export interface EdfInfo {
    date: Date;
    /** Channels the EDF header itself flags as bad (`meta.ch_bads`). Informational. */
    badChannels: string[];
    /**
     * Channels marked as Bad by users in the sEEG Preview (#400). Persisted on
     * the EDF, shared inside the institution; pre-deselected in New Analysis.
     */
    userBadChannels: string[];
    /**
     * `seeg` / `other` per channel, aligned with `channelNames` (#764). Decides which
     * channels may enter the Common Average / Median reference. Server-computed from the
     * shared rule; recomputed client-side from the same rule on an older backend.
     */
    channelKinds: ChannelKind[];
    channelNames: string[];
    annotations: RawEdfAnnotation[];
    length: number;
    sfreq: number;
    /** Native (pre-downsampling) sample rate, when recorded at upload time. Undefined for legacy uploads. */
    nativeSfreq?: number;
    patientId: number | undefined;
    lastUsed: string[] | null;
    title: string | null;
}

export interface RawEdfInfo {
    meta: {
        date: string;
        ch_bads: string[];
        ch_names: string[];
        annotations: RawEdfAnnotation[];
        len_sec: number;
        sfreq: number;
        native_sfreq?: number;
        /** Per-signal header fields captured at upload (#764); absent on legacy uploads. */
        ch_header?: ChannelHeader[];
    };
    last_used: string[] | null;
    /** User-marked Bad channels (#400); absent on older backends. */
    bad_channels?: string[];
    /** `seeg` / `other` per channel (#764); absent on older backends. */
    channel_kinds?: ChannelKind[];
    patient_id: number | null;
    title: string | null;
}

export interface RawEdfAnnotation {
    description: string;
    duration: number;
    onset: number;
}

export interface PatientMri {
    id: number;
    name: string;
    seg_ref?: string | null;
    /** Attached overlay volumes by role (#989): drives the data tree's upload/re-upload/remove
     *  actions for the post-implantation CT and MRI. */
    overlays?: Partial<Record<OverlayRole, boolean>>;
    /** Scan modality (#500). */
    modality?: 'mri' | 'ct';
}

export interface Patient {
    id: number;
    name: string;
    age: number | null;
    /** @deprecated #315 - No longer displayed in frontend UI. Column retained in database. */
    epilepsyType: string;
    /** @deprecated #315 - No longer displayed in frontend UI. Column retained in database. */
    surgeryOutput: string;
    etiology: string;
    implantationSummary: string;
    notes: string;
    mris: PatientMri[];
    /** When true, the patient is demo material and cannot be modified or deleted. */
    demo?: boolean;
}

// ========== Neuroimage / Coregistration Types ==========

export type NeuroimageFileType = 'pre_mri' | 'post_ct_mri' | 'implant_plan';

export type CoregistrationStatusValue = 'pending' | 'in_progress' | 'completed';

// Conversion lifecycle of a neuroimage file (#453). Mirrors the backend
// `ConversionStatus` enum `.value`s. `null` means no conversion was required
// (e.g. a native NIfTI upload) — distinct from `pending` (required, not started).
export type ConversionStatus = 'pending' | 'converting' | 'done' | 'failed';

export interface NeuroimageFile {
    id: number;
    patientId: number;
    userId: number;
    fileType: NeuroimageFileType;
    filename: string;
    mimeType: string | null;
    uploadedAt: number | null;
    uploaderName: string | null;
    conversionStatus: ConversionStatus | null;
    convertedNiftiRef: string | null;
    /** The coregistration request this file was uploaded for, if any (#620). */
    coregistrationId: number | null;
}

// Matches the backend `neuroimage_service.list_for_patient` response, which
// groups files by type (camelCase keys). Each entry is a NeuroimageFile dict.
export interface NeuroimageFilesResponse {
    preMri: NeuroimageFile[];
    postCtMri: NeuroimageFile[];
    implantPlan: NeuroimageFile[];
}

/** Flatten a NeuroimageFilesResponse into a single list of files. */
export function allNeuroimageFiles(res: NeuroimageFilesResponse | null | undefined): NeuroimageFile[] {
    if (!res) return [];
    return [...(res.preMri ?? []), ...(res.postCtMri ?? []), ...(res.implantPlan ?? [])];
}

export interface CoregistrationResult {
    id: number;
    patientId: number;
    status: CoregistrationStatusValue;
    mriRecordId: number | null;
    uploadedAt: number | null;
    name: string | null;
}

/**
 * Notification email recipient for coregistration requests
 */
export interface NotificationEmail {
    id: number;
    email: string;
}

// ============================================================================
// Dashboard types
// ============================================================================

export interface DashboardAnalysis {
    id: number;
    label: string;
    status: AnalysisStatus;
    montage: string | null;
    createdOn: number | null;
}
